import { z } from 'zod';

import { createOffsetPaginationRequestQuerySchema } from './pagination';
import { PrimaryKeySchema, commaDelimitedList } from './utils';

export const ResearchRequestEventTypesSchema = z.enum([
  // Generic event that reports a status to the user
  'generic',
]);

export type ResearchRequestEventTypes = z.infer<
  typeof ResearchRequestEventTypesSchema
>;

/**
 * Generic event that reports a status to the user
 *
 * Can include linked resources that the frontend can optionally link to if wanted
 */
const GenericResearchRequestEventSchema = z.object({
  message: z.string(),
  researchRequestQueryIds: PrimaryKeySchema.array().optional(),
  researchRequestHypothesisIds: PrimaryKeySchema.array().optional(),
  researchRequestEvidenceIds: PrimaryKeySchema.array().optional(),
  researchRequestEvidenceSourceIds: PrimaryKeySchema.array().optional(),
  researchRequestHypothesisEvidenceIds: PrimaryKeySchema.array().optional(),
});

const BaseResearchRequestEventSchema = z.object({
  researchRequestEventId: PrimaryKeySchema,
  researchRequestId: PrimaryKeySchema,
  eventType: ResearchRequestEventTypesSchema,
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const ResearchRequestEventSchema = z.discriminatedUnion('eventType', [
  BaseResearchRequestEventSchema.extend({
    eventType: ResearchRequestEventTypesSchema.extract(['generic']),
    eventData: GenericResearchRequestEventSchema,
  }),
]);

export type ResearchRequestEvent = z.infer<typeof ResearchRequestEventSchema>;

export const ResearchRequestEventsSchema = z.array(ResearchRequestEventSchema);

export type ResearchRequestEvents = z.infer<typeof ResearchRequestEventsSchema>;

export const ResearchRequestEventsQuerySchema = z
  .object({
    researchRequestEventIds: commaDelimitedList(
      PrimaryKeySchema.array(),
    ).optional(),
    researchRequestIds: commaDelimitedList(PrimaryKeySchema.array()).optional(),
    eventTypes: commaDelimitedList(
      ResearchRequestEventTypesSchema.array(),
    ).optional(),
  })
  .merge(createOffsetPaginationRequestQuerySchema(z.enum(['createdAt'])));

export type ResearchRequestEventsQuery = z.infer<
  typeof ResearchRequestEventsQuerySchema
>;
